// Copyright 2025. WebPros International GmbH. All rights reserved.

import { IVmResponse } from 'common/api/resources/ComputeResourceVm';
import { RootState } from 'admin/core/store';
import { LOADING_FLAGS } from 'common/modules/app/loadingFlags/constants';
import { isFeatureFlagEnabled } from 'common/modules/app/features/utils';
import { FEATURES } from 'common/modules/app/features/constants';
import {
    bindActionCreators,
    Dispatch,
} from 'redux';
import * as computeResourceVmActions from 'common/modules/computeResourceVm/actions';
import { connect } from 'react-redux';
import {
    Form,
    FormField,
    Translate,
} from '@plesk/ui-library';
import { SegmentedControl } from 'common/components/SegmentedControl/SegmentedControl';
import * as React from 'react';
import {
    ImageFormat,
    IStorageTypeResponse,
    KVMStorageTypes,
    STORAGE_TYPES_TRANSLATION_MAP,
    StorageType,
} from 'common/api/resources/StorageType';
import {
    INTENT_TYPE,
    SIZE,
} from 'common/constants';
import { Button } from 'admin/common/components/Button/Button';
import { VerticalIndent } from 'common/components/styles/VerticalIndent';
import { ErrorMessage } from 'admin/computeResourceVm/containers/BatchChangeStorageTypeForm/Styles';
import { VirtualizationType } from 'common/api/resources/ComputeResource';
import { TEST } from 'admin/computeResourceVm/constants';

interface IBatchChangeStorageTypeFormProps {
    servers: IVmResponse[];
    onSubmit?: () => void;
}

interface ISubmitValues {
    storage_type?: StorageType;
    image_format?: ImageFormat;
    servers: number[];
}

export type BatchChangeStorageTypeFormProps =
    IBatchChangeStorageTypeFormProps &
    ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

export const BatchChangeStorageTypeForm: React.FC<BatchChangeStorageTypeFormProps> = ({
    servers,
    computeResourceVmActions: {
        batchChangeStorageType,
    },
    sharedLvmEnabled,
    isRunningBatchChangeStorageType,
    onSubmit,
}) => {

    const [submitValues, setSubmitValues] = React.useState<ISubmitValues>({
        servers: servers.map(server => server.id),
    });

    const storageTypeOptions = React.useMemo(() => KVMStorageTypes.map(storage => ({
        title: STORAGE_TYPES_TRANSLATION_MAP[storage],
        value: storage,
    })).filter(item => sharedLvmEnabled || item.value !== StorageType.SHARED_LVM), [sharedLvmEnabled]);

    const availableStorageTypes = React.useMemo((): IStorageTypeResponse[] => {
        if (servers.length === 0) {
            return [];
        }

        let initialCr = servers[0].compute_resource;
        if (!initialCr || !initialCr.storages?.length) {
            return [];
        }

        return servers.map(server => server.compute_resource).reduce((acc, curr) => {
            if (!curr) {
                return [];
            }

            return acc.filter(storage => curr.storages?.some(item => item.type.name === storage.name));
        }, initialCr.storages.map(storage => storage.type));

    }, [servers]);

    const imageFormats = (storageType: StorageType) => {
        const storage = availableStorageTypes.find(item => item.name === storageType);

        if (!storage) {
            return [];
        }

        return storage.formats;
    };

    const vzServerSelected = React.useMemo(() => servers.some(server => server.virtualization_type === VirtualizationType.VZ), [servers]);

    const handleStorageTypeChange = (type: StorageType|undefined) => {
        if (!type) {
            return;
        }

        let imageFormat = submitValues.image_format;
        if (imageFormat && !imageFormats(type).includes(imageFormat)) {
            imageFormat = undefined;
        }

        setSubmitValues({
            ...submitValues,
            storage_type: type,
            image_format: imageFormat,
        });
    };

    const handleSubmit = async (values: ISubmitValues) => {
        if (!values.storage_type || !values.image_format) {
            return;
        }

        await batchChangeStorageType({
            ids: servers.map(server => server.id),
            storage_type: values.storage_type,
            image_format: values.image_format,
        });

        onSubmit && onSubmit();
    };

    return (
        <>
            <Translate content="computeResource.servers.batchChangeStorageTypeForm.subtitle" />
            <VerticalIndent />
            {vzServerSelected && (
                <ErrorMessage>
                    <Translate content="computeResource.servers.batchChangeStorageTypeForm.errors.vzIsNotSupported" />
                    <VerticalIndent />
                </ErrorMessage>
            )}
            {availableStorageTypes.length === 0 && (
                <ErrorMessage>
                    <Translate content="computeResource.servers.batchChangeStorageTypeForm.errors.noAvailableStorageTypes" />
                    <VerticalIndent />
                </ErrorMessage>
            )}
            <Form
                id="batchChangeStorageTypeForm"
                footerClassName="hidden"
                onSubmit={handleSubmit}
                values={submitValues}
                hideRequiredLegend={true}
                submitButton={false}
                cancelButton={false}
                applyButton={false}
                vertical={true}
            >
                <FormField
                    name="storage_type"
                    value={submitValues.storage_type}
                    label={
                        <Translate content="computeResource.servers.batchChangeStorageTypeForm.storageType" />
                    }
                    fullDescription={<Translate content="computeResource.servers.batchChangeStorageTypeForm.storageTypeDescription" />}
                >
                    <SegmentedControl
                        buttons={storageTypeOptions.map(option => ({
                            ...option,
                            disabled: vzServerSelected || !availableStorageTypes.map(type => type.name).includes(option.value),
                        }))}
                        selected={submitValues.storage_type}
                        onChange={handleStorageTypeChange}
                    />

                </FormField>
                <FormField
                    name="image_format"
                    label={
                        <Translate content="computeResource.servers.batchChangeStorageTypeForm.imageFormat" />
                    }
                >
                    <SegmentedControl
                        buttons={Object.values(ImageFormat).map((format) => ({
                            title: format,
                            value: format,
                            disabled: !submitValues.storage_type || !imageFormats(submitValues.storage_type).includes(format),
                        }))}
                        selected={submitValues.image_format}
                        onChange={(format) => setSubmitValues({ ...submitValues, image_format: format })}
                    />
                </FormField>
                <Button
                    type="submit"
                    form="batchChangeStorageTypeForm"
                    fill={true}
                    intent={INTENT_TYPE.PRIMARY}
                    size={SIZE.LG}
                    isLoading={isRunningBatchChangeStorageType}
                    disabled={vzServerSelected || !submitValues.storage_type || !submitValues.image_format}
                    data-cy={TEST.BATCH_CHANGE_STORAGE_FORM.SUBMIT_BUTTON}
                >
                    <Translate content="computeResource.servers.batchChangeStorageTypeForm.submit" />
                </Button>
            </Form>
        </>
    );
};

const mapStateToProps = (state: RootState) => ({
    isRunningBatchChangeStorageType: state.app.loadingFlags.has(LOADING_FLAGS.COMPUTE_RESOURCE_VM_CHANGE_STORAGE_TYPE),
    sharedLvmEnabled: isFeatureFlagEnabled(state, FEATURES.SHARED_LVM), // todo: SVM2-6489 remove this feature flag
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    computeResourceVmActions: bindActionCreators(computeResourceVmActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(BatchChangeStorageTypeForm);
